import type { ChatAudience } from '@/lib/types';
import { MATTER_STARTER_PROMPTS, STARTER_PROMPTS } from '@/lib/starterPrompts';
import { Icon } from './icons';

interface ChatWelcomeProps {
  audience: ChatAudience;
  /** Whether this browser has picked an audience yet; until it has, the choice comes first. */
  chosen: boolean;
  /** Matter workspace mode — starters are about the case files instead. */
  inMatter: boolean;
  onChooseAudience: (audience: ChatAudience) => void;
  /** Sends the starter as the first question. */
  onAsk: (question: string) => void;
  disabled?: boolean;
}

/**
 * The empty chat: who ALFIE is helping, then a handful of real first questions. A first-time
 * visitor picks "I'm a lawyer" or "I have a legal problem" (which sets the answer style);
 * after that the starters follow the audience toggle below the thread.
 */
export function ChatWelcome({ audience, chosen, inMatter, onChooseAudience, onAsk, disabled }: ChatWelcomeProps) {
  if (!chosen && !inMatter) {
    return (
      <section className="chat-welcome" aria-labelledby="chat-welcome-title">
        <h2 id="chat-welcome-title" className="chat-welcome-title">Who is ALFIE helping today?</h2>
        <p className="muted t-sm">Pick one and the answers are written for you. You can switch at any time below.</p>
        <div className="audience-choice">
          <button type="button" className="audience-option" onClick={() => onChooseAudience('PROFESSIONAL')}>
            <Icon.Scale aria-hidden />
            <span className="fw-semibold">I’m a lawyer</span>
            <span className="muted t-sm">Research, drafting and citation checks — answers cite what they rely on.</span>
          </button>
          <button type="button" className="audience-option" onClick={() => onChooseAudience('PLAIN')}>
            <Icon.Users aria-hidden />
            <span className="fw-semibold">I have a legal problem</span>
            <span className="muted t-sm">Where you stand and what to do next, in plain language.</span>
          </button>
        </div>
      </section>
    );
  }

  const prompts = inMatter ? MATTER_STARTER_PROMPTS : STARTER_PROMPTS[audience];
  return (
    <section className="chat-welcome" aria-labelledby="chat-welcome-title">
      <h2 id="chat-welcome-title" className="chat-welcome-title">
        {inMatter ? 'Ask about this matter' : audience === 'PLAIN' ? 'Describe what’s going on' : 'Ask a legal question'}
      </h2>
      <p className="muted t-sm">
        {inMatter
          ? 'Answers ground in the case documents filed under this matter and cite them.'
          : audience === 'PLAIN'
            ? 'General legal information, not legal advice — for your own situation, talk to a lawyer.'
            : 'Answers draw on the research library and cite what they rely on. Verify before you rely on them.'}
      </p>
      <div className="starter-prompts" role="group" aria-label="Starter questions">
        {prompts.map((p) => (
          <button key={p} type="button" className="starter-prompt" disabled={disabled} onClick={() => onAsk(p)}>
            {p}
          </button>
        ))}
      </div>
    </section>
  );
}
